import { useTranslation } from 'react-i18next';

import { CommonCard } from '@bonusly/common-give-box';
import { Box, BoxProps, Flex, Link, Spacer, VStack } from '@bonusly/design-system';

import { Bonus } from '../../../../../types/bonus';
import { Comment } from '../../components/comments/comment';
import { useCommentDisplay } from './hooks/use-comment-display';
import { useScrollToComment } from './hooks/use-scroll-to-comment';
import { LinkedFeedback } from './linked-feedback';

interface CommentsContainerProps extends BoxProps {
	bonus: Bonus;
	feedFilter?: string;
	parentBonus: Bonus;
	isModalDisplay?: boolean;
}

export const CommentsContainer = ({ bonus, feedFilter, parentBonus, isModalDisplay = false, ...rest }: CommentsContainerProps) => {
	const { t } = useTranslation('recognition');
	const { comments, displayed, onToggle, showAll, showToggle, total } = useCommentDisplay(bonus.child_bonuses, isModalDisplay, feedFilter);

	const scrollToCommentData = useScrollToComment();
	// Scroll only when the comment is rendered in the modal context
	const scrollToComment = isModalDisplay ? scrollToCommentData : {};
	const isForYouGivenFeed = feedFilter === 'for_you:given';

	return (
		<CommonCard variant="inlay" {...rest}>
			{!isModalDisplay && showToggle && (
				<Flex mb={3}>
					<Link as="button" color="text.soft" fontSize="md" fontWeight="medium" onClick={onToggle}>
						{isForYouGivenFeed
							? showAll
								? t('comment.hide_comments')
								: t('comment.show_all')
							: showAll
								? t('comment.hide_previous')
								: t('comment.show_previous')}
					</Link>
					<Spacer />
					<Box as="span" color="text.softer" fontWeight="normal">
						{t('comment.display_count', { displayed, total })}
					</Box>
				</Flex>
			)}

			<VStack align="left" spacing={4} data-testid="comment-stack">
				{bonus.evaluations?.map((evaluation) => <LinkedFeedback key={evaluation.id} evaluation={evaluation} />)}

				{comments.map((comment) => (
					<Comment
						bonus={comment}
						parentBonus={parentBonus}
						key={comment.id}
						scrollTo={comment.id === scrollToComment.commentId ? scrollToComment : undefined}
					/>
				))}
			</VStack>
		</CommonCard>
	);
};
